/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2024 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2024 - Finnish Meteorological Institute (FMI)
 * Copyright 2024 - The Norwegian Meteorological Institute (MET Norway)
 * */

import type {
  UnknownAction,
  ListenerEffectAPI,
  ThunkDispatch,
} from '@reduxjs/toolkit';
import { createListenerMiddleware, isAnyOf } from '@reduxjs/toolkit';
import { metronome } from '@opengeoweb/metronome';
import { dateUtils } from '@opengeoweb/shared';
import type { AnimationLengthSetting } from '@opengeoweb/time-slider';
import {
  AnimationLengthMode,
  defaultOffsetSelections,
  defaultTimeStep,
  getAnimationLengthFromRange,
  getSpeedDelay,
  isAnchoredMode,
  isValidAnimationLength,
  resolveAnimationRange,
  roundWithTimeStep,
  withModeSelections,
} from '@opengeoweb/time-slider';
import {
  handleDateUtilsISOString,
  isProjectionSupported,
  LayerType,
  webmapUtils,
  WMInvalidDateValues,
} from '@opengeoweb/webmap';
import {
  defaultLayers,
  IS_LEGEND_OPEN_BY_DEFAULT,
  mapEnums,
  mapSelectors,
} from '.';
import { setTime } from '../../generic/actions';
import type { LayerActions } from '../layer';
import { layerActions, layerSelectors } from '../layer';
import type {
  Layer,
  ReduxLayer,
  ToggleAutoUpdatePayload,
  WebMapStateModuleState,
} from '../types';
import type { Dimension } from './types';
import { LayerActionOrigin } from '../types';
import {
  ANIMATION_CLOCK_INTERVAL,
  endsAnimationClockOf,
  hasAnchoredAnimationLength,
  isSyncSkippedBy,
  mapIdToReconcile,
  reconcileAnimationRange,
} from './animationRangeReconciler';
import { filterLayers } from './filterLayers';
import { preserveAnimationStep, setStep } from './olMapListenerAnimationUtils';
import { mapActions } from './reducer';
import type { LayersAndAutoLayerIds } from './replaceLayerIdsToEnsureUniqueLayerIdsInStore';
import { replaceLayerIdsToEnsureUniqueLayerIdsInStore } from './replaceLayerIdsToEnsureUniqueLayerIdsInStore';
import { dateFormat, getAdjacentTimeValue } from './utils';
import { genericActions } from '../../generic';
import { SYNCGROUPS_TYPE_SETTIME } from '../../generic/syncGroups/constants';
import {
  getSynchronizationGroup,
  getSynchronizationGroupState,
  getSyncedMapIdsForTimeslider,
} from '../../generic/syncGroups/selectors';
import { uiActions, uiTypes } from '../../ui';
import type { DialogType } from '../../ui/types';

const getMapDimensionsFromLayers = (
  state: WebMapStateModuleState,
  mapId: string,
): Dimension[] => {
  const mapDimensions = mapSelectors.getMapDimensions(state, mapId) || [];
  const existingDimensionsByName = new Map(
    mapDimensions
      .filter((dimension) => dimension.name)
      .map((dimension) => [dimension.name!, dimension]),
  );

  return mapSelectors
    .getMapLayers(state, mapId)
    .reduce<Dimension[]>((dimensions, layer) => {
      layer.dimensions?.forEach((layerDimension) => {
        if (!layerDimension.name) {
          return;
        }

        const existingDimension = dimensions.find(
          (dimension) => dimension.name === layerDimension.name,
        );
        if (existingDimension) {
          return;
        }

        dimensions.push({
          name: layerDimension.name,
          currentValue:
            existingDimensionsByName.get(layerDimension.name)?.currentValue ??
            layerDimension.currentValue,
        });
      });

      return dimensions;
    }, []);
};

export const isAnimationEndTimeValid = (animationEndTime: string): boolean => {
  const hasValidPrefix =
    animationEndTime.split(/[-+]/)[0] === 'NOW' ||
    animationEndTime.split(/[-+]/)[0] === 'TODAY';
  const durationString = animationEndTime.substring(
    animationEndTime.indexOf('PT') + 2,
  );

  const hasValidDate = /^(\d+H)?(\d+M)?$/.test(durationString);

  if (hasValidPrefix && hasValidDate) {
    return true;
  }
  const parsedDate = dateUtils.parseISO(animationEndTime);
  return dateUtils.isValid(parsedDate);
};

export const mapListener = createListenerMiddleware<WebMapStateModuleState>();

mapListener.startListening({
  actionCreator: layerActions.layerDelete,
  effect: ({ payload }, listenerApi) => {
    const { mapId } = payload;

    const layers = mapSelectors.getMapLayersWithoutDimensionCurrentValue(
      listenerApi.getState(),
      mapId,
    );
    const isAnimating = mapSelectors.isAnimating(listenerApi.getState(), mapId);

    if (!layers.length && isAnimating) {
      listenerApi.dispatch(mapActions.mapStopAnimation({ mapId }));
    }
    updateAnimationMode(mapId, listenerApi);
  },
});

// update layer, synced maps and animation on latest max time if autoupdating
mapListener.startListening({
  actionCreator: layerActions.onUpdateLayerInformation,
  effect: ({ payload }, listenerApi) => {
    try {
      const { layerDimensions } = payload;
      if (!layerDimensions) {
        return;
      }
      const { dimensions, layerId } = layerDimensions;

      const layer = layerSelectors.getLayerById(
        listenerApi.getState(),
        layerId,
      );
      if (!layer) {
        return;
      }

      const newTimeDimension = dimensions.find(
        (dimension) => dimension.name === 'time',
      );
      const { mapId } = layer;

      if (!mapId) {
        return;
      }

      listenerApi.dispatch(
        mapActions.mapUpdateAllMapDimensions({
          mapId,
          dimensions: getMapDimensionsFromLayers(listenerApi.getState(), mapId),
          origin: 'mapListener/onUpdateLayerInformation',
        }),
      );

      const autoUpdateLayerId = mapSelectors.getAutoUpdateLayerId(
        listenerApi.getState(),
        mapId,
      );
      const autoTimeStepLayerId = mapSelectors.getAutoTimeStepLayerId(
        listenerApi.getState(),
        mapId,
      );
      const shouldAutoUpdate = mapSelectors.isAutoUpdating(
        listenerApi.getState(),
        mapId,
      );
      const prevTimeDimension = layerSelectors.getLayerTimeDimension(
        listenerApi.getState(),
        layerId,
      );

      const hasAutoLayers = !!autoUpdateLayerId && !!autoTimeStepLayerId;

      // Check if the explicitly set auto layers still exist in the layer store.
      // This distinguishes "caps not yet loaded" from "layer was deleted".
      const autoUpdateLayerExists =
        !autoUpdateLayerId ||
        !!layerSelectors.getLayerById(
          listenerApi.getState(),
          autoUpdateLayerId,
        );
      const autoTimeStepLayerExists =
        !autoTimeStepLayerId ||
        !!layerSelectors.getLayerById(
          listenerApi.getState(),
          autoTimeStepLayerId,
        );

      const hasTimeDimension = !!newTimeDimension;
      // Only auto-assign a leading layer when no auto layer is set yet,
      // or when the designated auto layer was removed from the map.
      const shouldSetAutoLayer =
        (!hasAutoLayers ||
          !autoUpdateLayerExists ||
          !autoTimeStepLayerExists) &&
        hasTimeDimension;

      if (shouldSetAutoLayer) {
        listenerApi.dispatch(mapActions.setAutoLayerId({ mapId, layerId }));
      }

      const effectiveAutoUpdateLayerId = shouldSetAutoLayer
        ? layerId
        : autoUpdateLayerId;
      const isAutoUpdateLayer = layerId === effectiveAutoUpdateLayerId;

      const incomingMaxTime = newTimeDimension?.maxValue;
      const isIncomingMaxTimeLaterThanCurrentLayerTime =
        incomingMaxTime &&
        prevTimeDimension?.currentValue &&
        prevTimeDimension.currentValue !== incomingMaxTime;

      if (
        isAutoUpdateLayer && // only update the active layer
        shouldAutoUpdate &&
        isIncomingMaxTimeLaterThanCurrentLayerTime
      ) {
        const isMapAnimating = mapSelectors.isAnimating(
          listenerApi.getState(),
          mapId,
        );
        if (!isMapAnimating) {
          listenerApi.dispatch(
            layerActions.layerChangeDimension({
              layerId,
              origin: LayerActionOrigin.updateLayerInformationListener,
              dimension: {
                name: 'time',
                currentValue: incomingMaxTime,
              },
            }),
          );
          // Each time a layer updates, set the new time for all synced timesliders
          const syncedMapIds: string[] = getSyncedMapIdsForTimeslider(
            listenerApi.getState(),
          );
          if (syncedMapIds) {
            // Change time value for all other timesliders
            syncedMapIds.map((syncedMapId) =>
              listenerApi.dispatch(
                setTime({
                  origin: LayerActionOrigin.updateLayerInformationListener,
                  sourceId: syncedMapId,
                  value: { currentTime: incomingMaxTime },
                }),
              ),
            );
          }
        }

        updateAnimation(mapId!, incomingMaxTime, listenerApi);
      }

      const state = listenerApi.getState();
      // Metadata updates affect all layers with the same name and service;
      // resolve each layer's time against its own map's selected time.
      Object.values(state.layers?.byId || {}).forEach((updatedLayer) => {
        if (
          !updatedLayer?.id ||
          !updatedLayer.mapId ||
          updatedLayer.name !== layer.name ||
          updatedLayer.service !== layer.service
        ) {
          return;
        }
        const wmLayer = webmapUtils.getWMLayerById(updatedLayer.id);
        const wmTimeDimension = wmLayer?.getDimension('time');
        const mapTime = mapSelectors.getMapDimension(
          state,
          updatedLayer.mapId,
          'time',
        );
        const timeDimension = updatedLayer.dimensions?.find(
          (dimension) => dimension.name === 'time',
        );
        if (
          wmLayer?.layerType !== LayerType.mapLayer ||
          !wmTimeDimension?.linked ||
          !mapTime?.currentValue ||
          !timeDimension
        ) {
          return;
        }
        const currentValue = wmTimeDimension.getClosestValue(
          mapTime.currentValue,
          true,
        );
        if (
          WMInvalidDateValues.has(currentValue) ||
          currentValue === timeDimension.currentValue
        ) {
          return;
        }
        listenerApi.dispatch(
          layerActions.layerSetDimensions({
            layerId: updatedLayer.id,
            origin: LayerActionOrigin.updateLayerInformationListener,
            dimensions: updatedLayer.dimensions!.map((dimension) =>
              dimension.name === 'time'
                ? { ...dimension, currentValue }
                : { ...dimension },
            ),
          }),
        );
      });
    } catch (error) {
      console.warn(error);
    }
  },
});

/** Auto follows the leading layer and the anchored modes follow the clock, so none may be shifted by layer updates. */
const derivesOwnAnimationRange = (
  animationLength: AnimationLengthSetting | undefined,
): boolean =>
  !!animationLength && animationLength.mode !== AnimationLengthMode.Exact;

const updateAnimation = (
  mapId: string,
  maxValue: string,
  listenerApi: ListenerEffectAPI<
    WebMapStateModuleState,
    ThunkDispatch<WebMapStateModuleState, unknown, UnknownAction>,
    unknown
  >,
): void => {
  const shouldEndtimeOverride = mapSelectors.shouldEndtimeOverride(
    listenerApi.getState(),
    mapId,
  );

  const shiftsWithLayerUpdates = (id: string): boolean =>
    !derivesOwnAnimationRange(
      mapSelectors.getAnimationLength(listenerApi.getState(), id),
    );

  if (shouldEndtimeOverride || !shiftsWithLayerUpdates(mapId)) {
    return;
  }

  const animationStart = mapSelectors.getAnimationStartTime(
    listenerApi.getState(),
    mapId,
  );

  // Calculate how much time the animation start need to move forwards
  const animationEnd = mapSelectors.getAnimationEndTime(
    listenerApi.getState(),
    mapId,
  );
  const animationEndUnix = dateUtils.unix(dateUtils.utc(animationEnd));
  const maxTimeAsUnix = dateUtils.unix(dateUtils.utc(maxValue));
  const timeInSecondToShiftAnimationForwards = maxTimeAsUnix - animationEndUnix;

  const animationStartUnix = dateUtils.unix(dateUtils.utc(animationStart));
  const newAnimationStartTime = dateUtils.dateToString(
    dateUtils.fromUnix(
      animationStartUnix + timeInSecondToShiftAnimationForwards,
    ),
    dateFormat,
  );

  const newAnimationEndTime = dateUtils.dateToString(
    dateUtils.fromUnix(animationEndUnix + timeInSecondToShiftAnimationForwards),
    dateFormat,
  );

  if (!newAnimationStartTime || !newAnimationEndTime) {
    return;
  }

  // Check if the mapId is part of a syncgroup
  const syncedMapIds: string[] = getSyncedMapIdsForTimeslider(
    listenerApi.getState(),
  );
  const isSyncedMap = syncedMapIds.includes(mapId);
  const mapIdsToUpdate = isSyncedMap ? syncedMapIds : [mapId];
  // Update new animation times for the map or in case of syncgroup, all maps in the syncgroup
  mapIdsToUpdate.filter(shiftsWithLayerUpdates).forEach((id) => {
    listenerApi.dispatch(
      mapActions.setAnimationEndTime({
        mapId: id,
        animationEndTime: newAnimationEndTime,
      }),
    );

    listenerApi.dispatch(
      mapActions.setAnimationStartTime({
        mapId: id,
        animationStartTime: newAnimationStartTime,
      }),
    );
  });
};

// Returns the maps among the given sync targets, in the order the sync groups dialog lists them
const getMapIdsInSyncListOrder = (
  state: WebMapStateModuleState,
  targetIds: string[],
): string[] => {
  const sourceIds = Object.keys(
    getSynchronizationGroupState(state)?.sources.byId ?? {},
  );
  const orderedTargetIds = [
    ...sourceIds.filter((sourceId) => targetIds.includes(sourceId)),
    ...targetIds.filter((targetId) => !sourceIds.includes(targetId)),
  ];
  return orderedTargetIds.filter((targetId) =>
    mapSelectors.getMapById(state, targetId),
  );
};

const isHigherSyncedMapAutoUpdating = (
  state: WebMapStateModuleState,
  mapId: string,
): boolean => {
  const syncedMapIds = getMapIdsInSyncListOrder(
    state,
    getSyncedMapIdsForTimeslider(state),
  );
  const mapIndex = syncedMapIds.indexOf(mapId);
  return syncedMapIds
    .slice(0, Math.max(mapIndex, 0))
    .some((syncedMapId) => mapSelectors.isAutoUpdating(state, syncedMapId));
};

mapListener.startListening({
  actionCreator: mapActions.toggleAutoUpdate,
  effect: async ({ payload }, listenerApi) => {
    listenerApi.cancelActiveListeners();

    // origin is passed on so that the presets of all affected maps get marked as changed
    const { shouldAutoUpdate, mapId, origin } = payload;
    if (!shouldAutoUpdate) {
      return;
    }
    try {
      const syncedMapIds: string[] = getSyncedMapIdsForTimeslider(
        listenerApi.getState(),
      );
      const autoUpdateLayerId = mapSelectors.getAutoUpdateLayerId(
        listenerApi.getState(),
        mapId,
      );
      const timeDimension = layerSelectors.getLayerTimeDimension(
        listenerApi.getState(),
        autoUpdateLayerId,
      );

      // go to end of active layer
      if (timeDimension?.maxValue && autoUpdateLayerId) {
        listenerApi.dispatch(
          layerActions.layerChangeDimension({
            layerId: autoUpdateLayerId,
            origin: LayerActionOrigin.toggleAutoUpdateListener,
            dimension: {
              name: 'time',
              currentValue: timeDimension.maxValue,
            },
          }),
        );

        // Change time value for all timesliders that are synced by syncgroups
        if (syncedMapIds.length > 0) {
          syncedMapIds.map((syncedMapId) =>
            listenerApi.dispatch(
              setTime({
                origin: LayerActionOrigin.toggleAutoUpdateListener,
                sourceId: syncedMapId,
                value: { currentTime: timeDimension.maxValue! },
              }),
            ),
          );
        }
        updateAnimation(mapId, timeDimension.maxValue, listenerApi);
      }

      // Toggle autoupdate off for synced timesliders
      const payloads: ToggleAutoUpdatePayload[] = syncedMapIds.reduce<
        ToggleAutoUpdatePayload[]
      >((syncedMapIdList, syncedMapId) => {
        if (
          syncedMapId !== mapId &&
          mapSelectors.isAutoUpdating(listenerApi.getState(), syncedMapId)
        ) {
          return syncedMapIdList.concat({
            mapId: syncedMapId,
            shouldAutoUpdate: false,
            origin,
          });
        }
        return syncedMapIdList;
      }, []);

      if (payloads.length > 0) {
        payloads.map((payload) =>
          listenerApi.dispatch(mapActions.toggleAutoUpdate(payload)),
        );
      }
    } catch (error) {
      console.warn(error);
    }
  },
});

mapListener.startListening({
  actionCreator: genericActions.syncGroupAddTarget,
  // When maps that auto update are synced, only the top one in the sync list keeps auto update
  effect: ({ payload }, listenerApi) => {
    const state = listenerApi.getState();
    const group = getSynchronizationGroup(state, payload.groupId);
    if (group?.type !== SYNCGROUPS_TYPE_SETTIME) {
      return;
    }

    const [, ...lowerAutoUpdatingMapIds] = getMapIdsInSyncListOrder(
      state,
      group.targets.allIds,
    ).filter((syncedMapId) => mapSelectors.isAutoUpdating(state, syncedMapId));

    // mark the presets of affected maps as changed when the user synced the maps
    const origin =
      payload.origin === 'user' ? mapEnums.MapActionOrigin.map : undefined;
    lowerAutoUpdatingMapIds.forEach((lowerMapId) =>
      listenerApi.dispatch(
        mapActions.toggleAutoUpdate({
          mapId: lowerMapId,
          shouldAutoUpdate: false,
          origin,
        }),
      ),
    );
  },
});

mapListener.startListening({
  actionCreator: mapActions.mapStartAnimation,
  effect: ({ payload }, listenerApi) => {
    const { mapId, initialTime } = payload;

    // Check if the mapId is part of a syncgroup
    const syncedMapIds: string[] = getSyncedMapIdsForTimeslider(
      listenerApi.getState(),
    );
    const isSyncedMap = syncedMapIds.includes(mapId);
    const autoUpdateMapId = syncedMapIds.find((syncedMapId) =>
      mapSelectors.isAutoUpdating(listenerApi.getState(), syncedMapId),
    );

    // Start animation with the mapId that started the animation or in case of syncgroup, the map that is auto updating
    const useMapId = isSyncedMap ? autoUpdateMapId || mapId : mapId;

    const speedDelay = mapSelectors.getMapAnimationDelay(
      listenerApi.getState(),
      useMapId,
    );
    const speed = 1000 / (speedDelay || 1);
    metronome.register(null, speed, useMapId);

    const timeList = mapSelectors.getAnimationList(
      listenerApi.getState(),
      useMapId,
    );

    // In case of the timeList
    if (timeList && timeList.length > 0) {
      // Determine animation step based on initialTime and timeList
      const initalTimerStep = timeList?.findIndex((timeNameValue) => {
        return timeNameValue.value === initialTime;
      });
      if (initalTimerStep !== -1) {
        setStep(useMapId, initalTimerStep);
      }
    }
  },
});

mapListener.startListening({
  actionCreator: mapActions.mapStopAnimation,
  effect: ({ payload: { mapId } }, listenerApi) => {
    // Check if the mapId is part of a syncgroup
    const syncedMapIds: string[] = getSyncedMapIdsForTimeslider(
      listenerApi.getState(),
    );
    const isSyncedMap = syncedMapIds.includes(mapId);

    const mapIdsToUnregister = isSyncedMap ? syncedMapIds : [mapId];
    mapIdsToUnregister.forEach((mapId) => metronome.unregister(mapId));
  },
});

const handleBaseLayers = (
  mapId: string,
  baseLayers: Layer[],
  listenerApi: ListenerEffectAPI<
    WebMapStateModuleState,
    ThunkDispatch<WebMapStateModuleState, unknown, LayerActions>
  >,
): void => {
  const baseLayer = baseLayers.find((layer) => layer.layerType === 'baseLayer');
  const currentAvailableBaseLayers =
    layerSelectors.getAvailableBaseLayersForMap(listenerApi.getState(), mapId);

  // find a availableBaseLayer with the same name, and use that id for the active baselayer
  const activeAvailableBaseLayer = currentAvailableBaseLayers.find(
    (availableBaseLayer: ReduxLayer) =>
      availableBaseLayer.name === baseLayer!.name,
  );

  const activeBaseLayerId = activeAvailableBaseLayer
    ? activeAvailableBaseLayer.id
    : webmapUtils.generateLayerId();

  // if the baseLayer can't be found in a visible available baseLayer list, add it
  // This happens when switching to a preset that has non-default baselayers
  if (!activeAvailableBaseLayer && currentAvailableBaseLayers.length) {
    listenerApi.dispatch(
      layerActions.addAvailableBaseLayers({
        layers: [{ ...baseLayer, mapId, id: activeBaseLayerId }],
      }),
    );
  }

  const baseLayersWithActiveId = baseLayers.map((layer, index) =>
    index === 0
      ? {
          ...layer,
          id: activeBaseLayerId || layer.id,
        }
      : layer,
  );

  listenerApi.dispatch(
    layerActions.setBaseLayers({
      mapId,
      layers: baseLayersWithActiveId,
    }),
  );
};

mapListener.startListening({
  actionCreator: mapActions.setMapPreset,
  effect: async ({ payload }, listenerApi) => {
    try {
      const { mapId, initialProps } = payload;
      const { mapPreset } = initialProps;

      if (mapPreset) {
        const {
          layers,
          activeLayerId,
          autoTimeStepLayerId,
          autoUpdateLayerId,
          proj,
          shouldAutoUpdate,
          shouldAnimate,
          animationPayload,
          toggleTimestepAuto,
          showTimeSlider,
          displayMapPin,
          shouldShowZoomControls,
          shouldShowLegend,
          legendPosition,
          legendSize,
          shouldShowDockedLayerManager,
          shouldShowDataExplorer,
          dockedLayerManagerSize,
          dockedLayerManagerPosition,
          dockedLayerManagerDimensions,
          timeSliderSpan,
          toggleTimeSpanAuto,
          defaultOverlayer,
          isClockVisible,
          isClockOptionsMenuVisible,
          clockPosition,
          clockSize,
          initialZoomToLayerId,
        } = mapPreset;

        const { mapLayers, baseLayers, overLayers } = filterLayers(layers);
        let pendingInitialZoomToLayerId = null;

        if (layers) {
          //  make sure all layers have a unique id before going forward
          let autoTimeStepLayerIdNew = autoTimeStepLayerId;
          let autoUpdateLayerIdNew = autoUpdateLayerId;
          const onlyActiveLayerIdIsSet =
            !autoTimeStepLayerId && !autoUpdateLayerId && activeLayerId;
          if (onlyActiveLayerIdIsSet) {
            autoTimeStepLayerIdNew = activeLayerId;
            autoUpdateLayerIdNew = activeLayerId;
          }

          const newLayerIds: LayersAndAutoLayerIds =
            replaceLayerIdsToEnsureUniqueLayerIdsInStore({
              layers: mapLayers,
              autoTimeStepLayerId: autoTimeStepLayerIdNew,
              autoUpdateLayerId: autoUpdateLayerIdNew,
            });

          listenerApi.dispatch(
            layerActions.setLayers({
              mapId,
              layers: newLayerIds.layers,
            }),
          );

          const newAutoTimeStepLayerId =
            newLayerIds.layers.find(
              (layer) => layer.id === newLayerIds.autoTimeStepLayerId,
            )?.id || undefined;

          const newAutoUpdateLayerId =
            newLayerIds.layers.find(
              (layer) => layer.id === newLayerIds.autoUpdateLayerId,
            )?.id || undefined;

          if (initialZoomToLayerId) {
            const initialZoomLayerIndex = mapLayers.findIndex(
              (layer) => layer.id === initialZoomToLayerId,
            );

            pendingInitialZoomToLayerId =
              initialZoomLayerIndex >= 0
                ? newLayerIds.layers[initialZoomLayerIndex]?.id
                : undefined;
          }

          listenerApi.dispatch(
            mapActions.setAutoLayerId({
              mapId,
              autoTimeStepLayerId: newAutoTimeStepLayerId,
              autoUpdateLayerId: newAutoUpdateLayerId,
            }),
          );
        }

        const customLayers = mapSelectors.getdefaultMapSettingsLayers(
          listenerApi.getState(),
        );
        const customBaseLayer = customLayers?.find(
          (layer) => layer.layerType === LayerType.baseLayer,
        );
        const customOverLayer = customLayers?.find(
          (layer) => layer.layerType === LayerType.overLayer,
        );

        // sets (default) baseLayers
        const baseLayersWithDefaultLayer = baseLayers.length
          ? baseLayers
          : [customBaseLayer || defaultLayers.baseLayerGrey];
        // sets (default) overLayers
        const overLayersWithDefaultLayer = overLayers!.length
          ? overLayers
          : [customOverLayer || defaultLayers.overLayer];

        const allBaseLayers = [
          ...baseLayersWithDefaultLayer,
          ...overLayersWithDefaultLayer!,
        ].map((layer) => ({
          ...layer,
          id: webmapUtils.generateLayerId(),
        })) as Layer[];

        handleBaseLayers(mapId, allBaseLayers, listenerApi);

        if (defaultOverlayer) {
          const overlayersFromStore = layerSelectors.getOverlayersForMapId(
            listenerApi.getState(),
            mapId,
          );

          const foundOverlayerId =
            overlayersFromStore.find(
              (overlayer) => overlayer.name === defaultOverlayer,
            )?.id || 'none';

          listenerApi.dispatch(
            mapActions.changeOverlayer({
              mapId,
              selectedOverlayer: foundOverlayerId,
              origin: 'mapListener',
            }),
          );
        }

        if (proj) {
          const checkIsprojectionSupported = isProjectionSupported(proj.srs);

          if (!checkIsprojectionSupported) {
            throw new Error(`Projection ${proj.srs} is not supported`);
          }

          listenerApi.dispatch(
            mapActions.setPendingInitialZoomToLayer({
              mapId,
              layerId: undefined,
            }),
          );

          //  set bbox
          listenerApi.dispatch(
            mapActions.setBbox({
              mapId,
              bbox: proj.bbox,
              srs: proj.srs,
            }),
          );
        } else {
          const layerId =
            pendingInitialZoomToLayerId ||
            // if not set in preset use first layer to determine initial bbox
            mapSelectors.getLayerIds(listenerApi.getState(), mapId).at(0);

          listenerApi.dispatch(
            mapActions.setPendingInitialZoomToLayer({
              mapId,
              layerId,
            }),
          );
        }

        const animationDuration = animationPayload?.duration;
        const animationLengthSetting = animationPayload?.animationLength;
        const animationEndTime =
          animationPayload?.endTime &&
          isAnimationEndTimeValid(animationPayload.endTime) &&
          animationPayload.endTime;
        const shouldEndtimeOverride = animationPayload
          ? animationPayload.shouldEndtimeOverride
          : false;

        if (shouldEndtimeOverride) {
          //  auto update
          listenerApi.dispatch(
            mapActions.setEndTimeOverriding({
              mapId,
              shouldEndtimeOverride,
            }),
          );
        }

        if (shouldAutoUpdate !== undefined && !animationEndTime) {
          //  auto update, unless a synced map higher in the sync list already auto updates
          listenerApi.dispatch(
            mapActions.toggleAutoUpdate({
              mapId,
              shouldAutoUpdate:
                shouldAutoUpdate &&
                !isHigherSyncedMapAutoUpdating(listenerApi.getState(), mapId),
            }),
          );
        }

        if (showTimeSlider !== undefined) {
          // toggle timeslider
          listenerApi.dispatch(
            mapActions.toggleTimeSliderIsVisible({
              mapId,
              isTimeSliderVisible: showTimeSlider,
            }),
          );
        }
        if (timeSliderSpan !== undefined) {
          // set timeslider span
          listenerApi.dispatch(
            mapActions.setTimeSliderSpan({
              mapId,
              timeSliderSpan: timeSliderSpan!,
            }),
          );
        }

        // toggle time span auto - defaults to true
        listenerApi.dispatch(
          mapActions.toggleTimeSpanAuto({
            mapId,
            timeSpanAuto: toggleTimeSpanAuto ?? true,
          }),
        );

        if (shouldShowZoomControls !== undefined) {
          // toggle zoom controls
          listenerApi.dispatch(
            mapActions.toggleZoomControls({ mapId, shouldShowZoomControls }),
          );
        }

        if (displayMapPin !== undefined) {
          //  display map pin
          listenerApi.dispatch(
            mapActions.toggleMapPinIsVisible({
              mapId,
              displayMapPin,
            }),
          );
        }

        if (isClockVisible !== undefined) {
          // toggle clock visibility
          listenerApi.dispatch(
            mapActions.toggleClock({
              mapId,
              isClockVisible,
            }),
          );
        }

        if (isClockOptionsMenuVisible) {
          // toggle clock options menu visibility
          listenerApi.dispatch(
            mapActions.toggleClockOptions({ mapId, isClockOptionsMenuVisible }),
          );
        }

        if (clockPosition !== undefined) {
          // set clock position
          listenerApi.dispatch(
            mapActions.setClockPositionSize({
              mapId,
              position: clockPosition,
            }),
          );
        }

        if (clockSize !== undefined) {
          // set clock size
          listenerApi.dispatch(
            mapActions.setClockPositionSize({
              mapId,
              size: clockSize,
            }),
          );
        }

        if (legendPosition !== undefined) {
          listenerApi.dispatch(
            mapActions.setLegendPositionSize({
              mapId,
              position: legendPosition,
            }),
          );
        }

        if (legendSize !== undefined) {
          listenerApi.dispatch(
            mapActions.setLegendPositionSize({
              mapId,
              size: legendSize,
            }),
          );
        }

        // sets timestep by interval of animationPayload
        const interval = animationPayload?.interval;
        if (interval) {
          listenerApi.dispatch(
            mapActions.setTimeStep({ mapId, timeStep: interval }),
          );
        }

        // Check if time dim already set (this is the case when duplicating view via Shift + split window)
        // If set, don't dispatch setTime action
        const timeAlreadySet = mapPreset.dimensions?.find(
          (dimension) => dimension.name === 'time',
        );
        if (animationEndTime || animationDuration) {
          // set animationEndTime by endTime of animationPayload
          // set animationStartTime by endTime minus duration of animationPayload

          const mode = ((): AnimationLengthMode => {
            if (animationEndTime) {
              if (animationEndTime.includes('TODAY')) {
                return AnimationLengthMode.RecurringPeriod;
              }
              if (animationEndTime.includes('NOW')) {
                return AnimationLengthMode.Now;
              }
              return AnimationLengthMode.Exact;
            }
            return AnimationLengthMode.Now;
          })();

          const endTimeFromPreset =
            animationEndTime &&
            (animationEndTime?.includes('NOW') ||
              animationEndTime?.includes('TODAY'))
              ? handleDateUtilsISOString(
                  dateUtils.convertNOWandTODAYFormatsToUTC(animationEndTime),
                )
              : animationEndTime;

          const endTime =
            endTimeFromPreset ||
            mapSelectors.getAnimationEndTime(listenerApi.getState(), mapId);

          if (!endTime) {
            // only happens if no map state was found
            return;
          }
          // startTime always relative to endTime
          const startTime = handleDateUtilsISOString(
            dateUtils
              .sub(dateUtils.utc(endTime), {
                minutes: animationDuration || 5 * 60, // default 5 hours
              })
              .toISOString(),
          );

          listenerApi.dispatch(
            mapActions.setAnimationLength({
              mapId,
              animationLength: getAnimationLengthFromRange(mode, {
                animationStartTime: startTime,
                animationEndTime: endTime,
              }),
              animationStartTime: startTime,
              animationEndTime: endTime,
              skipSync: !!animationLengthSetting,
            }),
          );

          if (timeAlreadySet === undefined) {
            listenerApi.dispatch(
              genericActions.setTime({
                origin: 'map/listener.ts, setMapPreset',
                sourceId: mapId,
                value: { currentTime: startTime },
              }),
            );
          }
        }

        // restores how the animation length is determined, overriding duration and endTime
        // if preset has no animation details at all: follow the auto-time-step layer if there is one, otherwise Now
        const hasNoAnimationDetails =
          !animationLengthSetting && !animationDuration && !animationEndTime;

        const defaultAnimationLengthSetting: AnimationLengthSetting =
          mapSelectors.getAutoTimeStepLayerId(listenerApi.getState(), mapId)
            ? { mode: AnimationLengthMode.Auto }
            : { mode: AnimationLengthMode.Now, now: defaultOffsetSelections };

        const effectiveAnimationLengthSetting:
          | AnimationLengthSetting
          | undefined = ((): AnimationLengthSetting | undefined => {
          if (animationLengthSetting) {
            return withModeSelections(animationLengthSetting);
          }
          if (hasNoAnimationDetails) {
            return defaultAnimationLengthSetting;
          }
          return undefined;
        })();

        if (effectiveAnimationLengthSetting) {
          const animationLength = effectiveAnimationLengthSetting;

          if (animationLength.mode === AnimationLengthMode.Exact) {
            listenerApi.dispatch(
              mapActions.setAnimationLength({
                mapId,
                animationLength,
                ...animationLength.exact,
                skipSync: true,
              }),
            );
            const { animationStartTime } = animationLength.exact;
            if (timeAlreadySet === undefined) {
              listenerApi.dispatch(
                genericActions.setTime({
                  origin: 'map/listener.ts, setAnimationLength',
                  sourceId: mapId,
                  value: { currentTime: animationStartTime },
                }),
              );
            }
          } else {
            const anchorInputs = mapSelectors.getAnimationAnchorInputs(
              listenerApi.getState(),
              mapId,
            );
            const resolvedRange =
              isAnchoredMode(animationLength.mode) &&
              isValidAnimationLength(animationLength, anchorInputs)
                ? resolveAnimationRange(animationLength, anchorInputs)
                : undefined;

            listenerApi.dispatch(
              mapActions.setAnimationLength({
                mapId,
                animationLength,
                ...resolvedRange,
                skipSync: true,
              }),
            );

            if (resolvedRange && timeAlreadySet === undefined) {
              listenerApi.dispatch(
                genericActions.setTime({
                  origin: 'map/listener.ts, setAnimationLength',
                  sourceId: mapId,
                  value: { currentTime: resolvedRange.animationStartTime },
                }),
              );
            }
          }
        }

        // sets animationDelay by speed of animationPayload
        if (animationPayload?.speed) {
          listenerApi.dispatch(
            mapActions.setAnimationDelay({
              mapId,
              animationDelay: getSpeedDelay(animationPayload.speed),
            }),
          );
        }

        //  turn animation on
        if (shouldAnimate === true) {
          listenerApi.dispatch(
            mapActions.mapStartAnimation({
              mapId,
              interval: interval || defaultTimeStep,
            }),
          );

          //  If animation interval set, set the timestep auto property to false
          if (interval) {
            listenerApi.dispatch(
              mapActions.toggleTimestepAuto({
                mapId,
                timestepAuto: false,
              }),
            );
          }
        } else if (toggleTimestepAuto !== undefined) {
          //  Set timestep auto based on preset if animation is off
          listenerApi.dispatch(
            mapActions.toggleTimestepAuto({
              mapId,
              timestepAuto: toggleTimestepAuto,
            }),
          );
        }

        // show legend
        const shouldOpenLegend =
          shouldShowLegend !== undefined
            ? shouldShowLegend
            : IS_LEGEND_OPEN_BY_DEFAULT;

        listenerApi.dispatch(
          uiActions.setActiveMapIdForDialog({
            type: `${uiTypes.DialogTypes.Legend}-${mapId}`,
            setOpen: shouldOpenLegend,
            mapId,
          }),
        );

        // show data explorer
        if (shouldShowDataExplorer) {
          listenerApi.dispatch(
            uiActions.setActiveMapIdForDialog({
              type: uiTypes.DialogTypes.DataExplorer,
              setOpen: true,
              source: 'app',
              mapId,
            }),
          );
        }

        if (dockedLayerManagerSize) {
          listenerApi.dispatch(
            mapActions.setDockedLayerManagerSize({
              mapId,
              dockedLayerManagerSize,
            }),
          );
        }

        const dockedLayerManagerType: DialogType = `${uiTypes.DialogTypes.DockedLayerManager}-${mapId}`;
        if (shouldShowDockedLayerManager !== undefined) {
          listenerApi.dispatch(
            uiActions.setToggleOpenDialog({
              type: dockedLayerManagerType,
              setOpen: shouldShowDockedLayerManager,
            }),
          );
        }

        if (shouldShowDockedLayerManager) {
          listenerApi.dispatch(
            uiActions.setToggleOpenDialog({
              type: uiTypes.DialogTypes.LayerManager,
              setOpen: false,
            }),
          );
        }

        if (dockedLayerManagerPosition) {
          listenerApi.dispatch(
            mapActions.setDockedLayerManagerPosition({
              mapId,
              dockedLayerManagerPosition,
            }),
          );
        }

        if (dockedLayerManagerDimensions) {
          listenerApi.dispatch(
            mapActions.setDockedLayerManagerDimensions({
              mapId,
              dockedLayerManagerDimensions,
            }),
          );
        }
      }
    } catch (error: unknown) {
      if (error instanceof Error) {
        listenerApi.dispatch(
          mapActions.setMapPresetError({
            mapId: payload.mapId,
            error: error.message,
          }),
        );
      }
    }
  },
});

mapListener.startListening({
  actionCreator: mapActions.setAnimationLength,
  effect: async ({ payload }, listenerApi) => {
    const { mapId } = payload;
    if (mapSelectors.isAnimating(listenerApi.getState(), mapId)) {
      setStep(mapId, -1);
    }

    while (hasAnchoredAnimationLength(listenerApi.getState(), mapId)) {
      // eslint-disable-next-line no-await-in-loop
      const endingAction = await listenerApi.take(
        endsAnimationClockOf(mapId),
        ANIMATION_CLOCK_INTERVAL,
      );
      if (endingAction) {
        return;
      }
      reconcileAnimationRange(mapId, listenerApi);
    }
  },
});

mapListener.startListening({
  actionCreator: mapActions.setAnimationStartTime,
  effect: ({ payload }, listenerApi) => {
    preserveAnimationStep(payload.mapId, listenerApi);
  },
});

mapListener.startListening({
  actionCreator: mapActions.setAnimationEndTime,
  effect: ({ payload }, listenerApi) => {
    preserveAnimationStep(payload.mapId, listenerApi);
  },
});

mapListener.startListening({
  actionCreator: mapActions.refreshAnimationRange,
  effect: ({ payload }, listenerApi) => {
    preserveAnimationStep(payload.mapId, listenerApi);
  },
});

mapListener.startListening({
  actionCreator: mapActions.unregisterMap,
  effect: ({ payload }, listenerApi) => {
    const { mapId } = payload;
    const layerList = layerSelectors.getLayersByMapId(
      listenerApi.getState(),
      mapId,
    );
    metronome.unregister(mapId);
    (layerList as ReduxLayer[]).map((layer) =>
      listenerApi.dispatch(
        layerActions.layerDelete({
          mapId,
          layerId: layer.id as string,
          origin: LayerActionOrigin.unregisterMapListener,
        }),
      ),
    );
    listenerApi.dispatch(uiActions.closeDialogsForMapId({ mapId }));
  },
});

mapListener.startListening({
  actionCreator: mapActions.setStepBackwardOrForward,
  effect: ({ payload }, listenerApi) => {
    const { mapId, isForwardStep } = payload;
    // timeStep is stored in minutes
    const timeStep = mapSelectors.getMapTimeStep(listenerApi.getState(), mapId);

    const currentTime = mapSelectors.getSelectedTime(
      listenerApi.getState(),
      mapId,
    );

    const [dataStartTime, dataEndTime] = mapSelectors.getDataLimitsFromLayers(
      listenerApi.getState(),
      mapId,
    );
    const hasValidDataStartTime =
      Number.isFinite(dataStartTime) &&
      dataStartTime > 0 &&
      dataStartTime < 2147483647;
    const hasValidDataEndTime = Number.isFinite(dataEndTime) && dataEndTime > 0;

    if (dateUtils.isValid(currentTime)) {
      const state = listenerApi.getState();
      const autoTimeStepLayerId = mapSelectors.getAutoTimeStepLayerId(
        state,
        mapId,
      );
      const autoTimeStepValues =
        mapSelectors.isTimestepAuto(state, mapId) && autoTimeStepLayerId
          ? layerSelectors.getLayerTimeDimension(state, autoTimeStepLayerId)
              ?.values
          : undefined;
      const isIrregularTimeList =
        !!autoTimeStepValues && !autoTimeStepValues.includes('/');

      const makeForwardStep = (): string => {
        // timeStep is in minutes, convert to seconds
        const timeStepInSeconds = timeStep * 60;
        const nextTime = currentTime + timeStepInSeconds;

        // For large timesteps (>12h), round to nearest hour instead of timestep
        // This ensures 12:07 with 24h step goes to 12:00 next day, not 00:00
        const roundingStep = timeStep > 12 * 60 ? 60 : timeStep; // Use 1 hour for large steps
        const roundedTime = roundWithTimeStep(nextTime, roundingStep, 'round');
        const newTime = hasValidDataEndTime
          ? Math.min(roundedTime, dataEndTime)
          : roundedTime;
        return dateUtils.fromUnix(newTime).toISOString();
      };

      const makeBackwardStep = (): string => {
        // timeStep is in minutes, convert to seconds
        const timeStepInSeconds = timeStep * 60;
        const nextTime = currentTime - timeStepInSeconds;

        // For large timesteps (>12h), round to nearest hour instead of timestep
        const roundingStep = timeStep > 12 * 60 ? 60 : timeStep; // Use 1 hour for large steps
        const roundedTime = roundWithTimeStep(nextTime, roundingStep, 'round');
        const newTime = hasValidDataStartTime
          ? Math.max(roundedTime, dataStartTime)
          : roundedTime;
        return dateUtils.fromUnix(newTime).toISOString();
      };

      const adjacentTime = isIrregularTimeList
        ? getAdjacentTimeValue(autoTimeStepValues, currentTime, !!isForwardStep)
        : undefined;
      if (isIrregularTimeList && adjacentTime === undefined) {
        return;
      }

      const getSelectedTimeString = (): string => {
        if (adjacentTime !== undefined) {
          return dateUtils.fromUnix(adjacentTime).toISOString();
        }
        return isForwardStep ? makeForwardStep() : makeBackwardStep();
      };
      const selectedTimeString = getSelectedTimeString();

      const isMapAnimating = mapSelectors.isAnimating(
        listenerApi.getState(),
        mapId,
      );
      if (isMapAnimating) {
        listenerApi.dispatch(
          mapActions.mapStopAnimation({
            mapId,
            origin: mapEnums.MapActionOrigin.map,
          }),
        );
      }
      listenerApi.dispatch(
        genericActions.setTime({
          origin: 'map/listener.ts, setStepBackwardOrForward',
          sourceId: mapId,
          value: { currentTime: handleDateUtilsISOString(selectedTimeString) },
        }),
      );
    }
  },
});

const updateAnimationMode = (
  mapId: string,
  listenerApi: ListenerEffectAPI<
    WebMapStateModuleState,
    ThunkDispatch<WebMapStateModuleState, unknown, UnknownAction>,
    unknown
  >,
): void => {
  const previousAutoTimeStepLayerId = mapSelectors.getAutoTimeStepLayerId(
    listenerApi.getOriginalState(),
    mapId,
  );
  const autoTimeStepLayerId = mapSelectors.getAutoTimeStepLayerId(
    listenerApi.getState(),
    mapId,
  );
  const animationLength = mapSelectors.getAnimationLength(
    listenerApi.getState(),
    mapId,
  );

  if (
    previousAutoTimeStepLayerId === autoTimeStepLayerId ||
    (animationLength?.mode !== AnimationLengthMode.ReferenceTime &&
      animationLength?.mode !== AnimationLengthMode.Auto)
  ) {
    return;
  }

  const autoTimeStepLayer = layerSelectors.getLayerById(
    listenerApi.getState(),
    autoTimeStepLayerId,
  );
  const referenceTimeDimension = autoTimeStepLayer?.dimensions?.find(
    (dimension) => dimension.name === 'reference_time',
  );

  if (autoTimeStepLayerId && referenceTimeDimension) {
    listenerApi.dispatch(
      mapActions.setAnimationLength({
        mapId,
        animationLength,
        ...resolveAnimationRange(animationLength, {
          referenceTime: referenceTimeDimension.currentValue,
        }),
      }),
    );
    return;
  }

  const nextAnimationLength: AnimationLengthSetting = autoTimeStepLayerId
    ? {
        mode: AnimationLengthMode.Auto,
      }
    : { mode: AnimationLengthMode.Now, now: defaultOffsetSelections };

  const anchorInputs = mapSelectors.getAnimationAnchorInputs(
    listenerApi.getState(),
    mapId,
  );
  const resolvedRange =
    nextAnimationLength.mode === AnimationLengthMode.Now &&
    isValidAnimationLength(nextAnimationLength, anchorInputs)
      ? resolveAnimationRange(nextAnimationLength, anchorInputs)
      : undefined;

  listenerApi.dispatch(
    mapActions.setAnimationLength({
      mapId,
      animationLength: nextAnimationLength,
      ...resolvedRange,
    }),
  );
};

mapListener.startListening({
  actionCreator: mapActions.setAutoLayerId,
  effect: ({ payload: { mapId } }, listenerApi) => {
    updateAnimationMode(mapId, listenerApi);
  },
});

mapListener.startListening({
  actionCreator: mapActions.setAutoTimestepLayerId,
  effect: ({ payload: { mapId } }, listenerApi) => {
    updateAnimationMode(mapId, listenerApi);
  },
});

/* Last, so the range is resolved after the listeners above have settled the setting. */
mapListener.startListening({
  matcher: isAnyOf(
    mapActions.setAnimationLength,
    mapActions.setTimeStep,
    mapActions.setAutoLayerId,
    mapActions.setAutoTimestepLayerId,
    layerActions.layerDelete,
    layerActions.onUpdateLayerInformation,
    layerActions.layerChangeDimension,
  ),
  effect: (action, listenerApi) => {
    const mapId = mapIdToReconcile(action, listenerApi.getState());
    if (mapId) {
      reconcileAnimationRange(mapId, listenerApi, isSyncSkippedBy(action));
    }
  },
});
